import { getContrastColor } from "@gettactic/helpers/src/colors/getContrastColor";
import React from "react";
import { MapInteractiveZone, SpotShape } from "../../../../types";
import { getEffectiveSpotAppearance } from "../../../../utils/spots";
import { computeAutomaticStateIconCenterOffset } from "../../../../utils/stateIconCentering";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import { DEFAULT_FONT_FAMILY, DEFAULT_NODE_CIRCLE_RADIUS } from "../../defines";
import { SpotLayout } from "../../types";
import { useShape } from "../ShapeContext/useShape";
import { InteractiveZoneSVG } from "./InteractiveZoneSVG";
import { SpotStateAreaSVG } from "./SpotStateAreaSVG";
import { useSpotColor } from "./useSpotColor";

export interface SpotItemSVGProps {
  item: SpotShape;
  layout: SpotLayout;
  isHighlighted?: boolean;
  interactiveZone?: MapInteractiveZone;
}

export const SpotItemSVG: React.FC<SpotItemSVGProps> = ({
  item,
  layout,
  interactiveZone,
  isHighlighted
}) => {
  const { isViewMode, highlightResource, setHoveringNode } = useMapCanvas();
  const { isHovered, isSelected, shape: _shape, selectIndex } = useShape();
  const shape = _shape as SpotShape;
  const { locked: isLocked } = shape;
  const rectColor = useSpotColor(shape);
  const [isSpotHovered, setIsSpotHovered] = React.useState(false);

  // Native interactivity (view mode only): the whole spot group — room rect,
  // label and state circle — is the click/hover target, mirroring the Konva
  // handlers in SpotShapeItem. Coordinates are viewport-space (from
  // getBoundingClientRect), which is what the hover/details poppers expect.
  // A picking mode can exclude the spot (e.g. a desk while the map is armed to add a
  // room): it stays visible but reads as unavailable — faded, no hover, no click.
  const isNotSelectable = !!interactiveZone?.isNotSelectable;
  const isInteractive =
    isViewMode && !shape.isInactive && !!shape.resource_id && !isNotSelectable;
  // Only claim clicks when a zone record provides an action; otherwise let
  // the event bubble so host-level delegated handlers (e.g. the mobile
  // webview's svg click delegation) can resolve the spot themselves.
  const isClickable = isInteractive && !!interactiveZone?.onAction;

  const handleClick = (event: React.MouseEvent<SVGGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const bounds = event.currentTarget.getBoundingClientRect();
    interactiveZone?.onAction?.({
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: bounds.height,
      isCmdOrCtrl: event.metaKey || event.ctrlKey
    });
  };

  const handleMouseEnter = (event: React.MouseEvent<SVGGElement>) => {
    setIsSpotHovered(true);
    const bounds = event.currentTarget.getBoundingClientRect();
    if (shape.resource_id && highlightResource) {
      highlightResource(shape.resource_id, {
        x: bounds.x,
        y: bounds.y,
        width: bounds.width,
        height: bounds.height
      });
    }
    setHoveringNode(shape);
  };

  const handleMouseLeave = () => {
    setIsSpotHovered(false);
    if (shape.resource_id && highlightResource) {
      highlightResource(null, { x: 0, y: 0 });
    }
    setHoveringNode(null);
  };

  const labelColor = React.useMemo(() => {
    return getContrastColor(rectColor);
  }, [rectColor]);

  const shapeStroke = isHighlighted
    ? "red"
    : !isLocked && (isHovered || isSelected)
      ? "#000000"
      : "transparent";

  const strokeWidth = isHighlighted ? 2 : 0.5;
  const spotType = getEffectiveSpotAppearance(
    item.spotType || shape.spotType,
    item.spotAppearance || shape.spotAppearance
  );

  const shapeWidth = (item.textProps?.width ?? layout.text.width) || 0;
  const isRoundRoom = spotType === "circle";
  const hasTextClip =
    spotType !== "meeting_room" && spotType !== "circle" && shapeWidth > 0;
  const labelPlacement = item.labelPlacement || shape.labelPlacement;

  // Matches InteractiveZoneSVG: a spot type with no rect of its own (Node)
  // has nothing that naturally scales/centers with a configured highlight
  // area — the editor-only debug/select-order markers below sit on the same
  // dot InteractiveZoneSVG draws, so they need the same auto-centering or
  // they'd float away from it once the real dot re-centers on a grid cell.
  const hasOwnRect = !!(layout.rect.width && layout.rect.height);
  const dotCenterOffset = !hasOwnRect
    ? computeAutomaticStateIconCenterOffset(shape)
    : null;
  const dotOffsetX = dotCenterOffset?.offsetX ?? 0;
  const dotOffsetY = dotCenterOffset?.offsetY ?? 0;
  const dotCircle = {
    x: layout.circle.x + dotOffsetX,
    y: layout.circle.y + dotOffsetY
  };

  const hasRect =
    spotType === "desk" ||
    spotType === "meeting_room" ||
    spotType === "parking_lot" ||
    spotType === "parking_space" ||
    isRoundRoom;

  let cornerRadius = item.rectProps?.cornerRadius || 0;
  if (spotType === "meeting_room") {
    cornerRadius = 15;
  } else if (isRoundRoom) {
    cornerRadius = 40;
  }

  const circleRadius = item.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS;

  const textFontSize = shape.fontSize || 16;
  const textFontFamily = shape.fontFamily || DEFAULT_FONT_FAMILY;
  const textFontWeight = shape.isBold ? "700" : "300";
  const textFontStyleAttr = shape.isItalic ? "italic" : "normal";
  const textRotation = item.textProps?.rotation ?? layout.text.rotation;
  const isLabelOutsideRect =
    labelPlacement === "outside" || labelPlacement === "outside-node";
  const defaultTextColor =
    item.textProps?.color ||
    (isLabelOutsideRect ? "black" : labelColor === "light" ? "white" : "black");
  const textColor = shape.textColor || defaultTextColor;

  // Calculate the transform for the entire spot group
  const transform = `translate(${shape.x}, ${shape.y})${shape.rotation ? ` rotate(${shape.rotation})` : ""}`;

  // Calculate proper text position based on alignment
  const marginLeft = item.textProps?.marginLeft || 0;
  const getTextPosition = () => {
    if (shape.textAlign === "center") {
      return layout.text.x + marginLeft + (layout.text.width || 0) / 2;
    }
    if (shape.textAlign === "right") {
      return layout.text.x + marginLeft + (layout.text.width || 0);
    }
    return layout.text.x + marginLeft;
  };

  const textX = getTextPosition();
  const textY =
    layout.text.y +
    (item.textProps?.marginTop || 0) +
    textFontSize / 2 -
    (spotType === "meeting_room" || spotType === "circle"
      ? textFontSize > 26
        ? textFontSize / 2
        : 0
      : 0);

  return (
    <g
      id={item.id}
      className={"spot-shape-container"}
      transform={transform}
      opacity={isNotSelectable ? 0.35 : undefined}
      onClick={isClickable ? handleClick : undefined}
      onMouseEnter={isInteractive ? handleMouseEnter : undefined}
      onMouseLeave={isInteractive ? handleMouseLeave : undefined}
      style={isInteractive ? { cursor: "pointer" } : undefined}
    >
      <g>
        {isRoundRoom ? (
          <circle
            cx={layout.rect.x + layout.rect.width / 2}
            cy={layout.rect.y + layout.rect.height / 2}
            r={layout.rect.width / 2}
            fill={rectColor}
            opacity={item.rectProps?.opacity || 1}
            stroke={shapeStroke}
            strokeWidth={strokeWidth}
          />
        ) : hasRect ? (
          <rect
            x={layout.rect.x}
            y={layout.rect.y}
            width={layout.rect.width}
            height={layout.rect.height}
            transform={`rotate(${layout.rect.rotation} ${layout.rect.x} ${layout.rect.y})`}
            rx={cornerRadius}
            fill={rectColor}
            opacity={item.rectProps?.opacity || 1}
            stroke={shapeStroke}
            strokeWidth={strokeWidth}
          />
        ) : null}
        <SpotStateAreaSVG
          shape={shape}
          layout={layout}
          interactiveZone={interactiveZone}
          hasRect={hasRect}
          cornerRadius={cornerRadius}
          isSpotHovered={isSpotHovered}
          isEditorPreview={!isViewMode}
        />
        {labelPlacement !== "hidden" && hasTextClip ? (
          <svg
            x={layout.text.x}
            y={layout.text.y - textFontSize}
            width={shapeWidth}
            height={textFontSize * 3}
            overflow="hidden"
          >
            <text
              x={textX - layout.text.x}
              y={
                textFontSize +
                (item.textProps?.marginTop || 0) +
                textFontSize / 2
              }
              transform={`rotate(${textRotation} ${textX - layout.text.x} ${textFontSize})`}
              style={{
                fontSize: `${textFontSize}px`,
                fontFamily: textFontFamily,
                fontWeight: textFontWeight,
                fontStyle: textFontStyleAttr,
                fill: textColor
              }}
              textAnchor={
                shape.textAlign === "center"
                  ? "middle"
                  : shape.textAlign === "right"
                    ? "end"
                    : "start"
              }
              dominantBaseline="middle"
            >
              {item.label}
            </text>
          </svg>
        ) : labelPlacement !== "hidden" ? (
          <text
            x={textX}
            y={textY}
            transform={`rotate(${textRotation} ${textX} ${layout.text.y})`}
            style={{
              fontSize: `${textFontSize}px`,
              fontFamily: textFontFamily,
              fontWeight: textFontWeight,
              fontStyle: textFontStyleAttr,
              fill: textColor
            }}
            textAnchor={
              shape.textAlign === "center"
                ? "middle"
                : shape.textAlign === "right"
                  ? "end"
                  : "start"
            }
            dominantBaseline="middle"
          >
            {item.label}
          </text>
        ) : null}
      </g>
      <g
        transform={
          shape.rotation
            ? `rotate(${-shape.rotation}, ${layout.circle.x}, ${layout.circle.y})`
            : undefined
        }
      >
        <InteractiveZoneSVG
          interactiveZone={interactiveZone}
          shape={shape}
          layout={layout}
          selectIndex={selectIndex}
          isActive={!shape.isInactive}
          isSpotHovered={isSpotHovered}
        />
        {selectIndex === undefined && !isViewMode && (
          <circle
            cx={dotCircle.x}
            cy={dotCircle.y}
            r={5}
            fill={item.resource_id ? "#60bb10" : "orange"}
            opacity={0.5}
          />
        )}
        {selectIndex !== undefined && (
          <g>
            <circle
              cx={dotCircle.x}
              cy={dotCircle.y}
              r={circleRadius}
              fill="#2563eb"
              stroke="#ffffff"
              strokeWidth={2}
            />
            <text
              x={dotCircle.x}
              y={dotCircle.y}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontSize: `${circleRadius * 0.8}px`,
                fontFamily: DEFAULT_FONT_FAMILY,
                fontWeight: "700",
                fill: "#ffffff"
              }}
            >
              {String(selectIndex + 1)}
            </text>
          </g>
        )}
      </g>
    </g>
  );
};
